/**
 * The Activities filter a user saved as their default (TRUS-762 round 4).
 *
 * Stored server-side in the existing per-(user, org) store
 * /api/user-filter-preferences/{page_key} — the same one the member list's
 * "Save my view" uses — so it follows the user to another device. The browser
 * copy (localStorage, per user) stays as the fallback when the call fails and
 * as the "last used" filter between visits.
 *
 * `filter_state` is opaque JSON to the server; this module owns its shape and
 * validates it on the way in, because a saved default outlives the code that
 * wrote it.
 */
import { apiClient } from '@/services/apiClient';
import { FEED_KINDS, type FeedKind } from '@/services/api/memberActivityFeed';

export const ACTIVITY_FILTERS_PAGE_KEY = 'member_activity_filters';

/** What the Filter popover edits. */
export interface FeedFilters {
  types: FeedKind[];
  /** One category: the API takes one, and it narrows to notes. */
  categoryId: number | null;
  dateFrom: string;
  dateTo: string;
}

/**
 * The case part of a saved default. A specific case is one member's, so only
 * the member-independent choices are saved: every case, or records with no case.
 */
export type SavedCaseMode = 'all' | 'none';

export interface SavedActivityFilters {
  filters: FeedFilters;
  caseMode: SavedCaseMode;
}

export const EMPTY_FILTERS: FeedFilters = { types: [], categoryId: null, dateFrom: '', dateTo: '' };

const isDate = (d: unknown) => typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d);

/** Anything in, a valid FeedFilters out (unknown kinds and bad dates dropped). */
export function parseFilters(v: unknown): FeedFilters {
  const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  return {
    types: Array.isArray(o.types) ? (o.types.filter((k) => FEED_KINDS.includes(k as FeedKind)) as FeedKind[]) : [],
    categoryId: typeof o.categoryId === 'number' ? o.categoryId : null,
    dateFrom: isDate(o.dateFrom) ? (o.dateFrom as string) : '',
    dateTo: isDate(o.dateTo) ? (o.dateTo as string) : '',
  };
}

export function isEmptyFilters(f: FeedFilters): boolean {
  return f.types.length === 0 && f.categoryId == null && !f.dateFrom && !f.dateTo;
}

/** The server's filter_state for this page, or null when nothing is saved. */
export function parseSaved(state: unknown): SavedActivityFilters | null {
  if (!state || typeof state !== 'object' || Object.keys(state as object).length === 0) return null;
  const o = state as Record<string, unknown>;
  const filters = parseFilters(o.filters ?? o);
  const caseMode: SavedCaseMode = o.caseMode === 'none' ? 'none' : 'all';
  if (isEmptyFilters(filters) && caseMode === 'all') return null;
  return { filters, caseMode };
}

/** The user's saved default. Throws when the store cannot be read (caller falls back). */
export async function loadSavedActivityFilters(): Promise<SavedActivityFilters | null> {
  const res = await apiClient.get<{ filter_state?: unknown }>(`/api/user-filter-preferences/${ACTIVITY_FILTERS_PAGE_KEY}`);
  return parseSaved(res.data?.filter_state);
}

export async function saveActivityFiltersDefault(saved: SavedActivityFilters): Promise<void> {
  await apiClient.put(`/api/user-filter-preferences/${ACTIVITY_FILTERS_PAGE_KEY}`, {
    filter_state: { filters: saved.filters, caseMode: saved.caseMode },
  });
}

export async function resetActivityFiltersDefault(): Promise<void> {
  await apiClient.delete(`/api/user-filter-preferences/${ACTIVITY_FILTERS_PAGE_KEY}`);
}

// ---- Browser copy (last used, and the fallback) ----------------------------

const filtersKey = (userId: unknown) => `member_activity_filters:${userId ?? 'anon'}`;

export function readLocalFilters(userId: unknown): FeedFilters {
  try {
    const raw = localStorage.getItem(filtersKey(userId));
    return raw ? parseFilters(JSON.parse(raw)) : EMPTY_FILTERS;
  } catch {
    return EMPTY_FILTERS;
  }
}

export function writeLocalFilters(userId: unknown, f: FeedFilters): void {
  try {
    localStorage.setItem(filtersKey(userId), JSON.stringify(f));
  } catch {
    // Private mode / blocked storage: the filter just is not remembered.
  }
}
